import React, { Suspense, useMemo } from 'react';
import {
  Tab,
  TabList,
  TabPanel,
  TabPanelList,
  TabProvider,
} from '@components/Shared/TabsCatalogueSkin';
import { ReportsPlaceholder } from 'chalktalk-react/Learnosity/LearnosityReports/Components/ReportHarness/styles';
import { TabGeneratorInterface, TabKey } from './types';

interface ReportTabsProps<TData extends TabKey> {
  data: TData[] | undefined;
  tabGenerator: TabGeneratorInterface<TData>;
  reportsComponent: React.ComponentType<{ data?: TData }>;
}

const ReportTabs = <TData extends TabKey>({
  data,
  tabGenerator,
  reportsComponent: ReportComponent,
}: ReportTabsProps<TData>) => {
  const tabKeys = useMemo(() => tabGenerator.generateTabs(data), [data, tabGenerator]);

  if (!tabKeys.length) {
    return null;
  }

  return (
    <TabProvider defaultTab={tabKeys[0].id}>
      <TabList>
        {tabKeys.map(({ id, name }) => (
          <Tab key={id} tabKey={id}>
            {name}
          </Tab>
        ))}
      </TabList>

      <TabPanelList>
        {tabKeys.map((tabKey) => {
          const selectedData = tabGenerator.findDataByTabKey(data, tabKey);

          return (
            <Suspense
              fallback={<ReportsPlaceholder>Loading...</ReportsPlaceholder>}
              key={tabKey.id}
            >
              <TabPanel tabKey={tabKey.id}>
                <ReportComponent data={selectedData} />
              </TabPanel>
            </Suspense>
          );
        })}
      </TabPanelList>
    </TabProvider>
  );
};

export default ReportTabs;
